import React, { useState, useRef, useEffect } from 'react';

// Generic imports
import _ from 'lodash';

// MUI imports
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faTrash, faCircleDollarToSlot, faBarcode, faClock, faCalendar } from '@fortawesome/pro-light-svg-icons';

// Local imports
import { DisplayCurrencySettingsOffer, BulkUpdateSettings } from './DisplayCurrencySettings.js';
import { CargowiseCodeTable } from './OpportunityCargowiseCodesModal.js';
import SpecialInputField from './SpecialInputField.js';
import OpportunityTitleEditModal from './OpportunityTitleEditModal.js';
import ConfirmDialog from '../components/ConfirmDialog.js';

function OfferSettingsDialog(props) {
    const { open, onClose, offer, setOffer, onDeleteOffer, organization, exchangeRates, lockedExchangeRates, sx } = props;
    const [ innerOffer, setInnerOffer ] = useState(_.cloneDeep(offer));
    const [ selectedCategory, setSelectedCategory ] = useState('general');
    const [ confirmDeleteOpen, setConfirmDeleteOpen ] = useState(false);
    const bulkUpdateRef = useRef(null);

    useEffect(() => {
        setConfirmDeleteOpen(false);
        setInnerOffer(_.cloneDeep(offer));
        setSelectedCategory('general');
    }, [ open, offer ]);
    
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth='lg' sx={sx}>
            <DialogContent sx={{ display: 'flex', padding: 0 }}>
                <Box sx={{ width: '300px', borderRight: '1px solid #e0e0e0' }}>
                    <Typography variant='h6' sx={{ padding: '16px' }}>Offer Settings</Typography>
                    <List>
                        <ListItem button selected={selectedCategory === 'general'} onClick={() => setSelectedCategory('general')}>
                            <FontAwesomeIcon icon={faCog} style={{ marginRight: '10px' }} />
                            <ListItemText primary='General' />
                        </ListItem>
                        <ListItem button selected={selectedCategory === 'cargowise'} onClick={() => setSelectedCategory('cargowise')}>
                            <FontAwesomeIcon icon={faBarcode} style={{ marginRight: '10px' }} />
                            <ListItemText primary='CargoWise Charge Codes' />
                        </ListItem>
                        <ListItem button selected={selectedCategory === 'currency'} onClick={() => setSelectedCategory('currency')}>
                            <FontAwesomeIcon icon={faCircleDollarToSlot} style={{ marginRight: '10px' }} />
                            <ListItemText primary='Currency' />
                        </ListItem>
                    </List>
                </Box>
                <Box sx={{ flexGrow: 1, padding: '16px', backgroundColor: 'rgb(250, 251, 252)', overflowY: 'auto' }}>
                    {selectedCategory === 'general' && (
                        <OfferSettingsGeneral offer={innerOffer} setOffer={setInnerOffer} />
                    )}
                    {selectedCategory === 'cargowise' && (
                        <OfferSettingCargowise offer={innerOffer} setOffer={setInnerOffer} />
                    )}
                    {selectedCategory === 'currency' && (
                        <>
                            <DisplayCurrencySettingsOffer offer={innerOffer} setOffer={setInnerOffer} organization={organization} exchangeRates={exchangeRates} lockedExchangeRates={lockedExchangeRates} />
                            <BulkUpdateSettings offer={innerOffer} ref={bulkUpdateRef} />
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ borderTop: '1px solid #e0e0e0', padding: '24px' }}>
                <Button
                    disabled={!onDeleteOffer}
                    color="error"
                    startIcon={<FontAwesomeIcon icon={faTrash} />}
                    onClick={() => {
                        setConfirmDeleteOpen(true);
                    }}
                >
                    Delete Offer
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={() => {
                    setOffer({ offer: innerOffer, currencyBulkUpdate: bulkUpdateRef.current?.get() });
                    onClose();
                }} sx={{ fontFamily: 'Poppins, sans-serif' }}>Save</Button>
            </DialogActions>
            <ConfirmDialog 
                open={confirmDeleteOpen} 
                onClose={() => setConfirmDeleteOpen(false)} 
                onConfirm={onDeleteOffer}
                color='error'
                sx={ sx?.zIndex ? { zIndex: sx.zIndex + 1 } : {}}
            >
                Are you sure you want to delete this offer?
            </ConfirmDialog>
        </Dialog>
    )
}

function OfferSettingsGeneral(props) {
    const { offer, setOffer } = props;
    const [ modal, setModal ] = useState({ open: false });

    // Allow setting the title and description of the offer
    return (
        <Box>
            <TextField
                fullWidth
                label='Title'
                value={offer.title}
                onChange={(e) => setOffer({ ...offer, title: e.target.value })}
                margin='normal'
            />
            <TextField
                fullWidth
                label='Description'
                value={offer.description}
                onChange={(e) => setOffer({ ...offer, description: e.target.value })}
                margin='normal'
                multiline
                rows={4}
            />
            <SpecialInputField
                icon={<FontAwesomeIcon icon={faClock} />}
                emptyStateText={'Transit Time'}
                title={'Set Transit Time'}
                label={'Transit Time'}
                path={`[0].transitTime`}
                inputType='transit-time'
                options={[]}
                optionsDescription={'Add a transit time to the entire offer. Cost rates transit times:'}
                modal={modal}
                setModal={setModal}
                offers={[offer]}
                setOffers={offers => setOffer(offers[0])}
                forceNoPadding={true}
            />

            <Box sx={{ margin: '0px 12px 0px 0px' }}>
                <SpecialInputField
                    icon={<FontAwesomeIcon icon={faCalendar} />}
                    emptyStateText={'Validity'}
                    title={'Set Validity'}
                    label={'Validity'}
                    path={`[0]`}
                    inputType='validity-range'
                    options={[]}
                    optionsDescription={'Let your customers know from and until what date the offer is valid. Cost rates validity:'}
                    modal={modal}
                    setModal={setModal}
                    offers={[offer]}
                    setOffers={offers => setOffer(offers[0])}
                />
            </Box>

            <OpportunityTitleEditModal
                modal={modal} 
                setModal={setModal} 
                offers={[offer]} 
                setOffers={offers => setOffer(offers[0])} 
                seaPorts={[]} 
                transportationMethod={''} 
            />
        </Box>
    )
}

function OfferSettingCargowise(props) {
    const { offer, setOffer } = props;

    return (
        <CargowiseCodeTable offer={offer} setOffer={setOffer} />
    )
}

export default OfferSettingsDialog;